import { useId, type ReactNode } from 'react';
import { ChevronLeftIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { OverlayScrollArea } from '@odo/shared-ui';
import { ModalShell } from '../../../../shared/ModalShell';

/** A modal over the page, or inline filling the host (an action screen). */
export type DmShellLayout = 'modal' | 'page';

interface DmModalShellProps {
    layout: DmShellLayout;
    title: string;
    subtitle?: ReactNode;
    /** Puts the subtitle beside the title, in the same subtitle type, instead of under it. */
    subtitleInline?: boolean;
    /** Next to the title, e.g. the monitoring status. */
    badge?: ReactNode;
    onBack?: () => void;
    onClose: () => void;
    /** Modal only: Escape and the overlay run this instead of back or close, e.g. to leave a step. */
    onDismiss?: () => void;
    footer: ReactNode;
    /** Modal only: Escape and the overlay close it only when true. */
    dismissible?: boolean;
    /** A write is in flight: close and back wait for it, so its result lands on an open modal. */
    busy?: boolean;
    /** The body holds side-by-side panes that scroll on their own, e.g. the teeth step. */
    panes?: boolean;
    /** Page only: the body brings its own header, e.g. the facts strip with its back arrow. */
    headerless?: boolean;
    /** Fixed between the header and the scrolling body, e.g. the facts strip. */
    pinned?: ReactNode;
    children: ReactNode;
}

const ICON_BUTTON =
    'rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-40';
/** On a page the content keeps a readable width, centred in wide hosts. */
const PAGE_WIDTH = 'mx-auto w-full max-w-6xl';
/** One size for every step and state, so switching between them never resizes the modal. */
const MODAL_HEIGHT = 'h-[min(85vh,860px)]';

export function DmModalShell({
    layout,
    title,
    subtitle,
    subtitleInline = false,
    badge,
    onBack,
    onClose,
    onDismiss,
    footer,
    dismissible = true,
    busy = false,
    panes = false,
    headerless = false,
    pinned,
    children
}: DmModalShellProps) {
    const titleId = useId();
    const isPage = layout === 'page';
    // On a page there is no close: back leaves the step, or the screen.
    const back = isPage ? (onBack ?? onClose) : onBack;
    const showHeader = !(isPage && headerless);
    let body: ReactNode;
    if (panes) {
        body = (
            <div className="flex-1 min-h-0 flex">
                <div className={`${isPage ? PAGE_WIDTH : 'w-full'} flex min-h-0`}>{children}</div>
            </div>
        );
    } else if (!isPage) {
        body = (
            <OverlayScrollArea className="flex-1 min-h-0" contentClassName="px-6" trackInsetPx={4}>
                {children}
            </OverlayScrollArea>
        );
    } else {
        body = (
            <OverlayScrollArea className="flex-1 min-h-0">
                <div className={`${PAGE_WIDTH} px-8 pb-8`}>{children}</div>
            </OverlayScrollArea>
        );
    }
    const content = (
        <>
            {showHeader && (
                <header
                    className={`flex items-start justify-between gap-4 border-b border-gray-200 pt-6 pb-4 ${
                        isPage ? `${PAGE_WIDTH} px-8` : 'px-6'
                    }`}
                >
                    <div className="flex items-start gap-2">
                        {back && (
                            <button
                                type="button"
                                onClick={back}
                                disabled={busy}
                                aria-label="Back"
                                className={`-ml-1.5 mt-0.5 ${ICON_BUTTON}`}
                            >
                                <ChevronLeftIcon className="h-5 w-5" />
                            </button>
                        )}
                        <div>
                            <div className="flex items-baseline gap-2.5">
                                <h2 id={titleId} className="text-xl font-bold text-gray-800">
                                    {title}
                                </h2>
                                {subtitle && subtitleInline && (
                                    <span className="text-sm text-gray-500">{subtitle}</span>
                                )}
                                {badge && <span className="self-center">{badge}</span>}
                            </div>
                            {subtitle && !subtitleInline && (
                                <p className="mt-0.5 text-sm text-gray-500">{subtitle}</p>
                            )}
                        </div>
                    </div>
                    {!isPage && (
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={busy}
                            aria-label="Close"
                            className={`-mr-1 ${ICON_BUTTON}`}
                        >
                            <XMarkIcon className="h-6 w-6" />
                        </button>
                    )}
                </header>
            )}
            {pinned && (
                <div className="shrink-0 border-b border-gray-200">
                    <div className={isPage ? `${PAGE_WIDTH} px-8` : 'px-6'}>{pinned}</div>
                </div>
            )}
            {body}
            <footer className="border-t border-gray-200">
                <div
                    className={`flex items-center justify-end gap-3 py-4 ${
                        isPage ? `${PAGE_WIDTH} px-8` : 'px-6'
                    }`}
                >
                    {footer}
                </div>
            </footer>
        </>
    );

    if (isPage) {
        return (
            <section
                aria-labelledby={showHeader ? titleId : undefined}
                aria-label={showHeader ? undefined : title}
                className="flex-1 min-h-0 flex flex-col bg-white"
            >
                {content}
            </section>
        );
    }
    return (
        <ModalShell
            widthClassName="max-w-[960px]"
            panelClassName={`rounded-lg flex flex-col ${MODAL_HEIGHT}`}
            dismissible={dismissible && !busy}
            onDismiss={onDismiss ?? onBack ?? onClose}
            labelledBy={titleId}
        >
            {content}
        </ModalShell>
    );
}
